import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getAvatarShape } from '@/lib/emojiMask';

import { LoginArea } from '@/components/auth/LoginArea';
import { DittoAvatar } from '@/components/DittoAvatar';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sparkles, Circle, Palette, User } from 'lucide-react';

const Index = () => {
  const { user, metadata } = useCurrentUser();
  const navigate = useNavigate();

  useSeoMeta({
    title: 'Ditto Profile Demo',
    description: 'Experience the Ditto design philosophy — profile themes, avatar shapes, and the most expressive profiles on Nostr.',
  });

  const fullMetadata = metadata ? { ...metadata } as Record<string, unknown> : undefined;
  const shape = getAvatarShape(fullMetadata);
  const displayName = metadata?.display_name || metadata?.name;

  const goToProfile = () => {
    if (user) {
      const npub = nip19.npubEncode(user.pubkey);
      navigate(`/${npub}`);
    }
  };

  return (
    <div className="min-h-screen bg-background overflow-hidden relative">
      {/* Animated background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-primary/8 blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-primary/5 animate-pulse-glow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-primary/8 animate-pulse-glow" style={{ animationDelay: '-1.5s' }} />
      </div>

      {/* Header */}
      <header className="relative z-10 px-4 sm:px-6 py-6 flex items-center justify-between max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center glow-primary">
            <Circle className="w-5 h-5 text-primary fill-primary" />
          </div>
          <span className="text-lg font-title font-bold tracking-tight">Ditto</span>
        </div>
        <LoginArea className="max-w-60" />
      </header>

      {/* Hero */}
      <main className="relative z-10 flex flex-col items-center justify-center px-4 sm:px-6 pt-12 sm:pt-24 pb-24">
        <div className="text-center max-w-2xl mx-auto space-y-8">
          {/* Floating Saturn symbol */}
          <div className="relative w-24 h-24 mx-auto mb-2">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/30 to-primary/5 animate-float" />
            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-primary/40 to-primary/10 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary text-glow" />
            </div>
            {/* Ring */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-8 rounded-full border border-primary/20 -rotate-12" />
          </div>

          <h1 className="text-5xl sm:text-7xl font-title font-bold leading-[1.1] tracking-tight">
            <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent">
              Your Profile
            </span>
            <br />
            <span className="text-glow">is a Planet</span>
          </h1>

          <p className="text-lg sm:text-xl text-muted-foreground max-w-lg mx-auto leading-relaxed">
            Customize your Nostr identity with expressive themes, avatar shapes, and a design language that{' '}
            <span className="text-primary">rejects boxes</span> in favor of
            circles.
          </p>

          {/* Feature pills */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <FeaturePill icon={<Palette className="w-3.5 h-3.5" />} label="Profile Themes" />
            <FeaturePill icon={<span className="text-sm">🎭</span>} label="Avatar Shapes" />
            <FeaturePill icon={<Sparkles className="w-3.5 h-3.5" />} label="Self Expression" />
          </div>

          {/* CTA */}
          {user ? (
            <div className="space-y-4 pt-4">
              <div className="flex items-center justify-center gap-3">
                <DittoAvatar
                  src={metadata?.picture}
                  alt={displayName || 'You'}
                  shape={shape}
                  size={48}
                  bordered
                />
                <div className="text-left">
                  <p className="text-sm text-muted-foreground">Logged in as</p>
                  <p className="font-semibold">{displayName || 'Anonymous'}</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  size="lg"
                  className="rounded-full glow-primary gap-2 text-base px-8"
                  onClick={goToProfile}
                >
                  View My Profile
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="rounded-full gap-2 border-primary/30 hover:bg-primary/10"
                  onClick={() => navigate('/settings')}
                >
                  <Palette className="w-4 h-4" />
                  Customize Theme
                </Button>
              </div>
            </div>
          ) : (
            <div className="pt-4 space-y-3">
              <p className="text-sm text-muted-foreground">
                Log in with your Nostr account to explore your profile
              </p>
              <div className="flex justify-center">
                <LoginArea className="max-w-72" />
              </div>
            </div>
          )}
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto mt-24 w-full">
          <FeatureCard
            icon={<Palette className="w-6 h-6" />}
            title="Profile Themes"
            description="Choose 3 colors and your entire profile transforms. Visitors see your world."
          />
          <FeatureCard
            icon={<span className="text-2xl">🔷</span>}
            title="Avatar Shapes"
            description="Set any emoji as your avatar's mask. Be a diamond, a heart, a star."
          />
          <FeatureCard
            icon={<User className="w-6 h-6" />}
            title="Your Identity"
            description="Every profile is a planet. Customize it with colors, fonts, and personality."
          />
        </div>

        {/* Footer */}
        <div className="mt-24">
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-muted-foreground/40 hover:text-muted-foreground/70 transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </div>
      </main>
    </div>
  );
};

function FeaturePill({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-sm text-primary">
      {icon}
      {label}
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="group relative p-6 rounded-2xl border border-border/50 bg-card/30 backdrop-blur-sm hover:bg-card/60 hover:border-primary/30 transition-all duration-300">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4 text-primary group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <h3 className="font-title font-semibold text-lg mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

export default Index;
